
import { useNavigate } from "react-router-dom";
import { collection, getDocs, query, where } from "firebase/firestore";
import { db } from "./firebase";

import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { APP_VERSION, APP_BUILD_DATE } from "./version";
import { brainScreenStyle } from "./brainBackground";

const INVALID_JOIN_CODE_MESSAGE =
  "No quiz is running with that code. Check the number on the host screen and try again.";

const JOIN_LOOKUP_NETWORK_MESSAGE =
  "Can't connect right now. Check your internet and try again.";

function isAppStandalone() {
  const host = window.location.hostname;
  return (
    (typeof window !== "undefined" && window.Capacitor) ||
    host === "localhost" ||
    host === "127.0.0.1" ||
    host === "::1" ||
    /^192\.168\./.test(host) ||
    /^10\./.test(host) ||
    /^172\.(1[6-9]|2\d|3[0-1])\./.test(host) ||
    window.matchMedia("(display-mode: standalone)").matches ||
    window.navigator.standalone === true
  );
}

export default function PlayerHome() {
  const [joinCode, setJoinCode] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [savedGame, setSavedGame] = useState(null);
  // Welcome / splash first in the installed app; join-code is the next step.
  const [screen, setScreen] = useState("welcome");
  const navigate = useNavigate();

  const standalone = isAppStandalone();

function readFirestoreValue(field = {}) {
  if ("stringValue" in field) return field.stringValue;
  if ("integerValue" in field) return Number(field.integerValue);
  if ("doubleValue" in field) return Number(field.doubleValue);
  if ("booleanValue" in field) return field.booleanValue;
  return undefined;
}

function readFirestoreDocument(row) {
  const doc = row?.document;
  if (!doc?.name) return null;
  const id = doc.name.split("/").pop();
  const fields = doc.fields || {};
  return {
    id,
    data: Object.fromEntries(
      Object.entries(fields).map(([key, value]) => [key, readFirestoreValue(value)])
    ),
  };
}

async function findGameByJoinCodeViaRest(code) {
  const response = await fetch(
    "https://firestore.googleapis.com/v1/projects/bobquiz-272a7/databases/(default)/documents:runQuery?key=AIzaSyC-LC0meTRyf50zFREASG6DsLKl4WU1DF8",
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        structuredQuery: {
          from: [{ collectionId: "games" }],
          where: {
            fieldFilter: {
              field: { fieldPath: "joinCode" },
              op: "EQUAL",
              value: { stringValue: code },
            },
          },
          limit: 10,
        },
      }),
    }
  );
  if (!response.ok) {
    const text = await response.text().catch(() => "");
    throw new Error(`REST ${response.status}: ${text.slice(0, 120)}`);
  }
  const rows = await response.json();
  for (const row of rows) {
    const parsed = readFirestoreDocument(row);
    if (!parsed) continue;
    if (parsed.data.joinCode === code && (parsed.data.state === "waiting" || parsed.data.state === "active")) {
      return parsed.id;
    }
  }
  return null;
}

async function fetchGameViaRest(gameCode) {
  const response = await fetch(
    `https://firestore.googleapis.com/v1/projects/bobquiz-272a7/databases/(default)/documents/games/${encodeURIComponent(gameCode)}?key=AIzaSyC-LC0meTRyf50zFREASG6DsLKl4WU1DF8`
  );
  if (response.status === 404) return null;
  if (!response.ok) throw new Error(`REST ${response.status}`);
  const json = await response.json();
  const fields = json.fields || {};
  return Object.fromEntries(
    ["state", "title", "name", "quizName", "current"].map((key) => [key, readFirestoreValue(fields[key])])
  );
}

// Check for saved game on mount
useEffect(() => {
  const savedTeam = localStorage.getItem("player_team");
  const savedGameCode = localStorage.getItem("player_gameCode");
  if (savedTeam && savedGameCode) {
    // Verify the game still exists and is active
    (async () => {
      try {
        // REST like the join-code lookup: the Firestore SDK silently never resolves in the native app WebView.
        const data = await fetchGameViaRest(savedGameCode);
        if (data) {
          if (data.state === "waiting" || data.state === "active") {
            setSavedGame({
              team: savedTeam,
              gameCode: savedGameCode,
              title: data.title || data.name || data.quizName || savedGameCode,
              currentQ: data.current || 0,
            });
          }
        }
      } catch (err) {
        console.error("Error checking saved game:", err);
      }
    })();
  }
}, []);




  // If not installed, show the install banner
if (!standalone) {
  return (
    <div
      style={{
        ...brainScreenStyle({
          gradient: "rgba(34,34,64,0.1), rgba(44,16,50,0.13)",
        }),
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "2vh 2vw",
        fontFamily: "'Montserrat','Open Sans',Arial,sans-serif",
      }}
    >
      <div
        style={{
          background: "rgba(10,10,20,0.65)",
          borderRadius: "26px",
          padding: "3em 3em",
          maxWidth: "540px",
          textAlign: "center",
          boxShadow: "0 4px 22px #000c",
        }}
      >
        <div
          style={{
            fontSize: "2em",
            fontWeight: 900,
            marginBottom: "1em",
            color: "#fff",
            textShadow: "0 0 4px #000, 0 1px 1px #000",
          }}
        >
          Welcome to the Quiz! Please install this app to play
        </div>

        <div style={{ marginBottom: "2em", fontSize: "1.2em", color: "#fff", textShadow: "0 0 3px #000" }}>
          <div style={{ fontWeight: 700, marginBottom: 6 }}>
            iPhone / iPad:
          </div>
          <div>
            Tap the <b>Share</b> button{" "}
            <svg
              viewBox="0 0 20 20"
              fill="none"
              style={{
                verticalAlign: "-0.22em",
                height: "1.2em",
                width: "1.2em",
                margin: "0 .16em",
              }}
            >
              <path
                d="M10 14V4M10 4L6 8M10 4l4 4"
                stroke="#fff"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <rect
                x="3"
                y="10"
                width="14"
                height="7"
                rx="2"
                stroke="#fff"
                strokeWidth="2"
              />
            </svg>{" "}
             at the bottom of your screen and then scroll up and select <b>"Add to Home Screen"</b>
          </div>
        </div>

        <div style={{ marginBottom: "2em", fontSize: "1.2em", color: "#fff", textShadow: "0 0 3px #000" }}>
          <div style={{ fontWeight: 700, marginBottom: 6 }}>
            Android:
          </div>
          <div>
            Tap <b>⋮</b> (top-right) then <b>Add to Home screen</b>
          </div>
        </div>

        <div
          style={{
            marginTop: "2em",
            fontWeight: 700,
            fontSize: "1.2em",
            color: "#fff",
            textShadow: "0 0 4px #000, 0 1px 1px #000",
          }}
        >
          Once installed, open the app from your home screen to join a quiz.
        </div>
      </div>
    </div>
  );
}



  // Handle form submit
  async function handleJoin(e) {
    e.preventDefault();
    setError("");
    setLoading(true);

    const code = joinCode.trim();
    if (!/^\d{4}$/.test(code)) {
      setError("Please enter a 4-digit code.");
      setLoading(false);
      return;
    }

    // Search Firestore for a game with this joinCode
    try {
      const restFound = await findGameByJoinCodeViaRest(code);
      if (restFound) {
        setLoading(false);
        navigate(`/play/${restFound}`);
        return;
      }

      // Native app uses REST only — SDK often hangs in Capacitor WebView.
      if (typeof window !== "undefined" && window.Capacitor) {
        setLoading(false);
        setError(INVALID_JOIN_CODE_MESSAGE);
        return;
      }

      const gamesByCode = query(collection(db, "games"), where("joinCode", "==", code));
      const snapshot = await Promise.race([
        getDocs(gamesByCode),
        new Promise((_, reject) => setTimeout(() => reject(new Error("SDK lookup timed out")), 10000)),
      ]);
      let found = null;
      snapshot.forEach(doc => {
        const data = doc.data();
        if (
          data.joinCode === code &&
          (data.state === "waiting" || data.state === "active")
        ) {
          found = doc.id;
        }
      });

      setLoading(false);

      if (found) {
        navigate(`/play/${found}`);
      } else {
        setError(INVALID_JOIN_CODE_MESSAGE);
      }
    } catch (err) {
      const msg = String(err?.message || err || "");
      if (msg.includes("timed out") || msg.includes("SDK lookup")) {
        setError(INVALID_JOIN_CODE_MESSAGE);
      } else if (/failed to fetch|network|REST 5|REST 4/i.test(msg)) {
        setError(JOIN_LOOKUP_NETWORK_MESSAGE);
      } else {
        setError(INVALID_JOIN_CODE_MESSAGE);
      }
      setLoading(false);
    }
  }

  // Installed app: welcome / splash before join-code entry
  if (standalone && screen === "welcome") {
    return (
      <div
        style={{
          ...brainScreenStyle(),
          width: "100%",
          maxWidth: "100%",
          margin: 0,
          boxSizing: "border-box",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexDirection: "column",
          padding: "max(2vh, env(safe-area-inset-top)) 24px max(2vh, env(safe-area-inset-bottom))",
          fontFamily: "'Montserrat','Open Sans',Arial,sans-serif",
          overflowX: "hidden",
        }}
      >
        <div
          style={{
            width: "100%",
            maxWidth: 360,
            margin: "0 auto",
            boxSizing: "border-box",
            textAlign: "center",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            padding: "1.5em 0 2em",
          }}
        >
          <img
            src="/quizmaster-bob-logo.png"
            alt="Quizmaster Bob"
            style={{
              width: 120,
              height: 120,
              objectFit: "cover",
              borderRadius: "50%",
              marginBottom: 18,
              border: "3px solid rgba(255,255,255,0.85)",
              boxShadow: "0 8px 28px rgba(0,0,0,0.45)",
              display: "block",
            }}
          />
          <div
            style={{
              fontSize: "1.85em",
              fontWeight: 900,
              color: "#fff",
              textShadow: "0 0 8px #000, 0 2px 12px #000",
              marginBottom: "1.5em",
              lineHeight: 1.2,
              width: "100%",
              textAlign: "center",
            }}
          >
            Welcome to Quizmaster Bob&apos;s trivia
          </div>

          {savedGame && (
            <div
              style={{
                marginBottom: "1.2em",
                padding: "1.2em",
                width: "100%",
                boxSizing: "border-box",
                background: "rgba(153, 255, 204, 0.15)",
                borderRadius: "1em",
                border: "2px solid rgba(153, 255, 204, 0.4)",
                textAlign: "left",
              }}
            >
              <div style={{ fontSize: "1.05em", fontWeight: 800, marginBottom: "0.5em", color: "#fff", textShadow: "0 1px 4px #000" }}>
                Continue where you left off
              </div>
              <div style={{ fontSize: "0.95em", marginBottom: "0.9em", color: "rgba(255,255,255,0.9)", textShadow: "0 1px 3px #000" }}>
                Team: <strong>{savedGame.team}</strong><br />
                Quiz: {savedGame.title}<br />
                Current question: {savedGame.currentQ + 1}
              </div>
              <button
                type="button"
                onClick={() => navigate(`/play/${savedGame.gameCode}`)}
                style={{
                  background: "linear-gradient(135deg, #99ffc9, #009a63 88%)",
                  color: "#222",
                  fontWeight: 800,
                  fontSize: "1.15rem",
                  border: "none",
                  borderRadius: "1.5em",
                  padding: "0.65em 1.5em",
                  cursor: "pointer",
                  width: "100%",
                  boxShadow: "0 2px 8px #0002",
                }}
              >
                Continue quiz
              </button>
              <button
                type="button"
                onClick={() => {
                  localStorage.removeItem("player_team");
                  localStorage.removeItem("player_gameCode");
                  localStorage.removeItem("player_selfieUrl");
                  localStorage.removeItem("player_wizardStep");
                  setSavedGame(null);
                }}
                style={{
                  background: "transparent",
                  color: "rgba(255,255,255,0.75)",
                  fontWeight: 600,
                  fontSize: "0.9rem",
                  border: "none",
                  cursor: "pointer",
                  marginTop: "0.7em",
                  width: "100%",
                  textDecoration: "underline",
                }}
              >
                Dismiss and join a different quiz
              </button>
            </div>
          )}

          <button
            type="button"
            onClick={() => setScreen("join")}
            style={{
              background: savedGame
                ? "rgba(255,255,255,0.14)"
                : "linear-gradient(135deg, #99ffc9, #009a63 88%)",
              color: savedGame ? "#fff" : "#222",
              fontWeight: 800,
              fontSize: "1.25rem",
              border: savedGame ? "2px solid rgba(255,255,255,0.35)" : "none",
              borderRadius: "2em",
              padding: "0.75em 2em",
              cursor: "pointer",
              width: "100%",
              maxWidth: 360,
              boxSizing: "border-box",
              boxShadow: "0 3px 16px #0002",
              marginTop: savedGame ? 4 : 0,
            }}
          >
            {savedGame ? "Enter a join code" : "Continue"}
          </button>
        </div>

        <div
          style={{
            position: "fixed",
            bottom: 8,
            left: 0,
            right: 0,
            textAlign: "center",
            fontSize: "0.72rem",
            color: "rgba(255,255,255,0.55)",
            fontFamily: "monospace",
            zIndex: 5,
          }}
        >
          <span style={{ pointerEvents: "none" }}>
            Player app v{APP_VERSION} · {APP_BUILD_DATE}
          </span>
          {" · "}
          <Link to="/privacy" style={{ color: "rgba(153,255,201,0.75)", textDecoration: "underline" }}>
            Privacy
          </Link>
          {" · "}
          <Link to="/support" style={{ color: "rgba(153,255,201,0.75)", textDecoration: "underline" }}>
            Support
          </Link>
        </div>
      </div>
    );
  }

  // UI — join-code screen
  return (
    <div
      style={{
        ...brainScreenStyle(),
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexDirection: "column",
      }}
    >
      <form
        onSubmit={handleJoin}
        style={{
          background: "transparent",
          padding: "2em 2em 2.5em 2em",
          maxWidth: 430,
          width: "96vw",
          textAlign: "center",
        }}
      >
        {standalone && (
          <button
            type="button"
            onClick={() => setScreen("welcome")}
            style={{
              background: "transparent",
              border: "none",
              color: "rgba(255,255,255,0.7)",
              fontWeight: 700,
              fontSize: "0.95rem",
              cursor: "pointer",
              marginBottom: 12,
              textDecoration: "underline",
            }}
          >
            ← Back
          </button>
        )}
        <div style={{
          fontSize: "2em",
          fontWeight: 900,
          marginBottom: 18,
          color: "#fff",
          textShadow: "0 0 8px #000, 0 2px 12px #000",
        }}>
          Enter the join code from the Quizmaster's host screen
        </div>
        <input
          type="text"
          inputMode="numeric"
          pattern="\d{4}"
          maxLength={4}
          value={joinCode}
          onChange={e => setJoinCode(e.target.value.replace(/\D/g, ""))}
          placeholder="4-digit code"
          autoFocus
          style={{
            width: "180px",
            fontSize: "2em",
            letterSpacing: "0.4em",
            textAlign: "center",
            borderRadius: "1em",
            border: "2px solid rgba(255,255,255,0.45)",
            marginBottom: "1.2em",
            fontWeight: 700,
            background: "rgba(255,255,255,0.92)",
          }}
        />
        <br />
        <button
          type="submit"
          disabled={loading}
          style={{
            background: "linear-gradient(135deg, #99ffc9, #009a63 88%)",
            color: "#222",
            fontWeight: 700,
            fontSize: "1.45rem",
            border: "none",
            borderRadius: "2em",
            padding: "0.7em 2.3em",
            boxShadow: "0 3px 16px #0002, 0 2px 6px #fff3 inset",
            cursor: loading ? "wait" : "pointer",
            marginTop: 2,
            transition: "filter 0.2s",
            filter: loading ? "brightness(0.93)" : "brightness(0.98)",
          }}
        >
          {loading ? "Joining..." : "Join Quiz"}
        </button>
        {error && (
          <div style={{ color: "#ffb3b3", marginTop: 14, fontWeight: 700, textShadow: "0 1px 4px #000" }}>
            {error}
          </div>
        )}
        {savedGame && (
          <div style={{
            marginTop: "2em",
            padding: "1.5em",
            background: "rgba(153, 255, 204, 0.15)",
            borderRadius: "1em",
            border: "2px solid rgba(153, 255, 204, 0.4)",
          }}>
            <div style={{ fontSize: "1.1em", fontWeight: 700, marginBottom: "0.8em", color: "#fff", textShadow: "0 1px 4px #000" }}>
              Or continue previous game
            </div>
            <div style={{ fontSize: "0.95em", marginBottom: "1em", color: "rgba(255,255,255,0.9)", textShadow: "0 1px 3px #000" }}>
              Team: <strong>{savedGame.team}</strong><br />
              Quiz: {savedGame.title}<br />
              Current Question: {savedGame.currentQ + 1}
            </div>
            <button
              type="button"
              onClick={() => navigate(`/play/${savedGame.gameCode}`)}
              style={{
                background: "linear-gradient(135deg, #99ffc9, #009a63 88%)",
                color: "#222",
                fontWeight: 700,
                fontSize: "1.1rem",
                border: "none",
                borderRadius: "1.5em",
                padding: "0.6em 1.8em",
                cursor: "pointer",
                width: "100%",
                boxShadow: "0 2px 8px #0002",
              }}
            >
              Continue quiz
            </button>
            <button
              type="button"
              onClick={() => {
                localStorage.removeItem("player_team");
                localStorage.removeItem("player_gameCode");
                localStorage.removeItem("player_selfieUrl");
                localStorage.removeItem("player_wizardStep");
                setSavedGame(null);
              }}
              style={{
                background: "transparent",
                color: "rgba(255,255,255,0.75)",
                fontWeight: 600,
                fontSize: "0.9rem",
                border: "none",
                cursor: "pointer",
                marginTop: "0.8em",
                textDecoration: "underline",
              }}
            >
              Clear saved game
            </button>
          </div>
        )}
      </form>
      {standalone && (
        <div
          style={{
            position: "fixed",
            bottom: 8,
            left: 0,
            right: 0,
            textAlign: "center",
            fontSize: "0.72rem",
            color: "rgba(255,255,255,0.55)",
            fontFamily: "monospace",
            zIndex: 5,
          }}
        >
          <span style={{ pointerEvents: "none" }}>
            Player app v{APP_VERSION} · {APP_BUILD_DATE}
          </span>
          {" · "}
          <Link
            to="/privacy"
            style={{
              color: "rgba(153,255,201,0.75)",
              textDecoration: "underline",
              pointerEvents: "auto",
            }}
          >
            Privacy
          </Link>
          {" · "}
          <Link
            to="/support"
            style={{
              color: "rgba(153,255,201,0.75)",
              textDecoration: "underline",
              pointerEvents: "auto",
            }}
          >
            Support
          </Link>
        </div>
      )}
    </div>
  );
}
